// The combat recipe kit: the arithmetic every gun shot and every hit draws
// with (R1007, `ui-map-interface.md` §6.3.3).
//
// A SHOT look flies the rounds of one salvo from the shooter to the victim. A
// HIT look is what one round draws when it arrives. `combat-styles.ts` is the
// registry that answers a World theme look id;
// `combat-recipes-{melee,artillery,sentinel,shield,hull,sink}.ts` hold the
// draw code of the picked cards of mockup rounds 048 to 053. This module is
// what all seven import: the row and the context types, the eases, the hash,
// the two alpha clamps, the four shared inks, the atlas cell keys and every
// primitive a card needs.
//
// This is a leaf (`leaves.test.ts`). It names the three sinks of a layer
// through a type-only clause, the way `aura-styles.ts` does. The layer owns
// the state, the clock, the cull, the atlas and the pools, and it hands them
// in as one reused context. The clamps, the eases and the sink primitives
// every family shares stand in `recipe-kit.ts`; this module binds them to
// `[combat.limits]` and adds what a gun alone draws.
//
// **Nothing here allocates.** A recipe runs once a round a frame, so every
// scratch is a module constant and every hash is integer arithmetic (R131).
//
// **A recipe is a pure function of its own progress and the layer clock.** It
// reads `c.p`, `c.t`, `c.k` and `c.seed`, and keeps nothing between frames.
//
// **The layer reads no reduced-motion switch, and that is deliberate.** An
// effect freezes under it and keeps its mark (R915), because an effect is a
// standing decoration and the mark is what it says. A round in flight IS the
// event: a frozen round would hang over the water for ever and say nothing at
// all. The marks are short instead — a salvo lands inside the second its own
// HIT event covers — so there is no long motion to take away.
//
// ---------------------------------------------------------------------------
// HOW A MOCKUP `draw(c, …)` MAPS ONTO THE KIT
// ---------------------------------------------------------------------------
//
// The cards are canvas-2D. Every one of them calls the page kit `K` (a shot)
// or `HK` (a hit), and this module is `K` and `HK` over the three instanced
// batches. The translation is fixed:
//
//   | the card writes            | the recipe writes                      |
//   | ------------------------- | -------------------------------------- |
//   | `K.ink(c)`                | `c.ink` (already resolved)             |
//   | `K.dir(from, to)`         | `c.d` (built once per round per frame) |
//   | `K.at(from, d, u, off)`   | `atOn(c.d, c.from, u, off, OUT)`       |
//   | `K.disc` / `HK.disc`      | `disc(c, …)` → the circle sink         |
//   | `K.ring` / `HK.ring`      | `ring(c, …)` → the circle sink         |
//   | `K.arc` / `HK.arc`        | `arcOf(c, …)` → the circle sink        |
//   | `K.line` / `HK.line`      | `line(c, …)` → the segment sink        |
//   | a line astern of the head | `trail(c, …)` → one segment            |
//   | a raw polyline            | `polyline(c, …)` → n − 1 segments      |
//   | `HK.zig`                  | `zig(c, …)` → the same                 |
//   | `K.flare` / `HK.flare`    | `flare(c, …)` → the `combat:flare` cell|
//   | `HK.fog`                  | `fog(c, …)` → the same cell, squashed  |
//   | `K.blob` (an ellipse)     | `blob(c, …)` → the `combat:disc` cell  |
//   | `fillRect` under a rotate | `rect(c, …)` → the `white` cell        |
//   | `K.dart`                  | `dart(c, …)` → the `combat:dart` cell  |
//   | `HK.poly` (a fragment)    | `plate(c, …)` → the `combat:plate` cell|
//   | `K.glint`                 | `glint(c, …)` → two segments           |
//   | a hex outline             | `hexOutline(c, …)` → six segments      |
//   | `HK.hull` / `HK.descend`  | `hull(c, …)` → the baked silhouette    |
//   | `HK.erase`                | NOTHING: the layer suppresses the hull |
//   | `HK.wreck`               | `wreck(c.segs, …)` of `chart-glyphs.ts`|
//
// A gradient is the one thing a batch cannot make, so the three radial ramps
// of the cards are baked atlas cells that the quad sink tints and scales
// (`fast/gfx/atlas.ts combatSpecFor`). `HK.erase` has no twin at all: it
// exists only because the mockup harness had already painted the hull, and on
// the map the layer takes the hull instance away instead (R1007).
//
// THE WORKED EXAMPLE, card 048/10 POINT-BLANK FLASH, line for line:
//
//   the card                                    the recipe
//   ------------------------------------------- --------------------------
//   const ink = K.ink(c)                        c.ink
//   const d = K.dir(from, to)                   c.d
//   const h = K.at(from, d, p)                  atOn(c.d, c.from, c.p, 0, P0)
//   const f = 1 - p                             const f = 1 - c.p
//   K.line(from, to, ink, 0.5 * f, 5 * f)       line(c, from, to,
//                                                 row.widthPx * f, c.ink,
//                                                 row.alpha * f)
//   K.line(from, to, WHITE, .85*f*f, 2.2*f+.4)  line(c, …, WHITE,
//                                                 row.alpha2 * f * f)
//   K.flare(from, 10 * f + 2, WHITE, 0.9 * f)   flare(c, from, row.lengthPx*f
//                                                 + MUZZLE_FLOOR, WHITE, …)
//   K.disc(h, 1.8, WHITE, 0.95)                 disc(c, h, row.rPx, WHITE, …)
//
// Every FIGURE came out of the card and now stands in
// `data/presentation/combat.toml`. Every RATIO stayed in the recipe file as a
// named constant: `MUZZLE_FLOOR` above is the card's `+ 2`, and the `f * f`
// is the shape of the fade. That is the rule the effect recipes follow.

import { COMBAT_LIMITS, NEUTRAL_COLOR } from '../gen/presentation';
import type { CombatHitRow, CombatShotRow } from '../gen/presentation';
import type { SpriteEntry } from '../fast/layers/batches';
import {
  apexFor, arcFor, cellKeys, clamp01, dashedRingFor, discFor, fillFor, lineFor,
  polylineFor, rectFor, ringFor, softFor, strokeFor, TAU,
  type Pt, type SinkCtx,
} from './recipe-kit';
import { wreck } from './chart-glyphs';

export {
  DEEP_INK, PALE_INK, SOLID_KEY, TAU, cellScale, clamp01, mixInk, type Pt,
} from './recipe-kit';

/** The white core of a flash, the one ink a card names that is not a look. */
export const COMBAT_WHITE = 0xffffff;

// The four inks of §2 the cards name beside the faction colour and the white
// core. A combat look wears no theme: the canvas is never themed (R1007).
// `PALE_INK` (a trail, a bow wave, a wash, a thread and a cooled spark) and
// `DEEP_INK` are the shared kit's, passed on above.

/** The neutral grey: silt, smoke and a part that wears no faction. */
export const NEUTRAL_INK = NEUTRAL_COLOR;
/** Foam: cold water, not fire. */
export const FOAM_INK = 0xcfe8f2;

export type { CombatHitRow, CombatShotRow };

// ---------------------------------------------------------------------------
// The atlas cells
// ---------------------------------------------------------------------------

/** The five baked marks a recipe draws from, all baked WHITE. */
const COMBAT_CELLS = ['flare', 'dome', 'disc', 'dart', 'plate'] as const;
export type CombatCell = (typeof COMBAT_CELLS)[number];
const COMBAT_CELL_KEYS = cellKeys('combat', COMBAT_CELLS);

/**
 * The atlas key of one baked combat cell.
 *
 * The scheme lives here and `fast/gfx/atlas.ts combatSpecFor` answers it: one
 * spelling, two readers. It is the rule `effectCellKey` follows.
 */
export function combatCellKey(cell: CombatCell): string {
  return COMBAT_CELL_KEYS[cell];
}

/**
 * The side of the `flare`, `dome` and `disc` cells, in world px at scale 1.
 *
 * One size serves every card: a recipe scales the quad to the radius its row
 * states, so a figure change bakes nothing new.
 */
export const COMBAT_CELL_PX = 64;

/**
 * The `dart` cell's natural box: the nose at x = 0, the tail at x = −1, the
 * half-width 0.5. A recipe scales it to the length and the half-width its
 * card draws, so `dart` states nothing about the shape.
 */
export const COMBAT_DART_LEN = 1;

/**
 * The `plate` cell's natural half-length. The fragment of mockup 052/04 is
 * `[s, 0, −0.6 s, 0.5 s, −s, −0.4 s]`, so the cell is that shape at s = 1.
 */
export const COMBAT_PLATE_HALF = 1;

// ---------------------------------------------------------------------------
// The victim's hull
// ---------------------------------------------------------------------------

/**
 * The victim's hull, as a hit recipe reads it.
 *
 * The layer fills it in from the fleet layer and the look's ship icon set. A
 * recipe reads it and keeps no reference. `sprite` answers the baked
 * silhouette — filled, or stroked for an outline card — and `bloom` the halo
 * that comes with a filled hull, so a hull a recipe redraws matches the one
 * the fleet layer took away.
 */
export interface HullMark {
  /** The hull's place in world px. It is the last place it was seen. */
  x: number;
  y: number;
  /** Its heading in radians: +x is the bow, +y is starboard. */
  head: number;
  /** Its owner's faction ink. */
  ink: number;
  /** Its half length and half beam in world px, for a reach off the plate. */
  halfLenPx: number;
  halfBeamPx: number;
  /** The baked silhouette. `hollow` gives the stroked master. */
  sprite(hollow: boolean): SpriteEntry;
  /** The halo that comes with the filled silhouette. */
  bloom(): SpriteEntry;
}

/** What a hull draw may change beside the transform. */
export interface HullDrawOpts {
  /** Draws the stroked master in place of the fill. */
  hollow?: boolean;
  /** Draws the halo under the fill, as the fleet layer does. */
  halo?: boolean;
  /** Keeps the slice `[x0, x1]` of the hull in ITS OWN x, in world px. */
  clipX0?: number;
  clipX1?: number;
  /** Additive blend, for a mark that must read as light. */
  add?: boolean;
}

// ---------------------------------------------------------------------------
// The contexts
// ---------------------------------------------------------------------------

/** The geometry of one flight: the unit vector, its normal and the length. */
export interface Dir {
  ux: number; uy: number;
  nx: number; ny: number;
  len: number;
  ang: number;
}

/** What every recipe may draw with. The layer reuses ONE of each (R131). The
 *  fade `alpha`, the additive flag, the three sinks and the atlas cell lookup
 *  are the `SinkCtx` of the shared kit. */
export interface CombatCtx extends SinkCtx {
  /** The layer's clock in seconds. A strobe, a spin and a dash phase read it. */
  t: number;
  /** The camera scale: CSS px for one world px. */
  z: number;
  /** The ink already resolved: the shooter's faction for a shot, the
   *  victim's for a hit. */
  ink: number;
}

/** One round in flight. */
export interface ShotCtx extends CombatCtx {
  row: CombatShotRow;
  /** The muzzle and the round's own target point, in world px. */
  from: Pt;
  to: Pt;
  /** The round's flight progress, 0 to 1. */
  p: number;
  /** The round's index in the salvo. */
  k: number;
  /** A stable seed for every hash of this round. */
  seed: number;
  /** The geometry of the flight, built once a round a frame. */
  d: Dir;
}

/** One hit mark. */
export interface HitCtx extends CombatCtx {
  row: CombatHitRow;
  /** Where the round arrived, in world px. */
  at: Pt;
  /** The unit vector from the shooter to the victim. */
  dirX: number;
  dirY: number;
  /** The mark's life progress, 0 to 1. */
  p: number;
  /** The round's index in its own salvo, 0 for the first and for a sink. */
  k: number;
  /** A stable seed for every hash of this mark. */
  seed: number;
  /** The victim's hull, or null where the viewer cannot see it. */
  hull: HullMark | null;
  /**
   * What the victim's shields still hold, 0 to 1, or −1 where the viewer
   * cannot read them (R1011).
   *
   * The layer reads it off the victim's own state every frame where the feed
   * answers, and it keeps the figure of the event where the contact went dark.
   * A card that states the shield — a crack that grows, a ring that stutters,
   * an ink that drains — reads this and never the mark's own age.
   */
  shieldFrac: number;
  /**
   * True while THIS mark is the one that may redraw the whole hull (R1011).
   *
   * A HIT event is one salvo, so a victim carries one mark per round and a
   * look that redraws the silhouette would stack three to ten of them. The
   * layer names the NEWEST mark of each victim, and every older mark of that
   * hull draws its own strike alone.
   */
  lead: boolean;
}

/** One shot look's draw code, keyed by its look id. */
export type ShotRecipeFn = (c: ShotCtx) => void;

/** One hit look's draw code, keyed by its look id. */
export type HitRecipeFn = (c: HitCtx) => void;

// ---------------------------------------------------------------------------
// The arithmetic
// ---------------------------------------------------------------------------

/** Fast start, slow end. It is `K.easeOut` and `HK.out`. */
export function out(p: number): number {
  const u = clamp01(p);
  return 1 - (1 - u) * (1 - u);
}

/** Slow start, fast end. It is `K.easeIn` and `HK.inn`. */
export function inn(p: number): number {
  const u = clamp01(p);
  return u * u;
}

/** 0, up, back to 0. It is `HK.hump`. */
export function hump(p: number): number {
  return Math.sin(Math.PI * clamp01(p));
}

/** Holds to `from`, then falls to 0 at 1. It is `HK.tail`. */
export function tail(p: number, from: number): number {
  return p < from ? 1 : 1 - (p - from) / (1 - from);
}

/**
 * A pure hash of two integers, 0 to 1. It is `K.hash` and `HK.h`, byte for
 * byte, so a card's scatter is the card's own scatter.
 */
export function hash(i: number, k: number): number {
  let x = ((i | 0) * 374761393 + (k | 0) * 668265263) | 0;
  x = Math.imul(x ^ (x >>> 13), 1274126177);
  return ((x ^ (x >>> 16)) >>> 0) / 4294967296;
}

/** The geometry of a flight, written into `outDir`. It is `K.dir`. */
export function dirOf(from: Pt, to: Pt, outDir: Dir): Dir {
  const dx = to.x - from.x, dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  outDir.ux = dx / len; outDir.uy = dy / len;
  outDir.nx = -dy / len; outDir.ny = dx / len;
  outDir.len = len;
  outDir.ang = Math.atan2(dy, dx);
  return outDir;
}

/**
 * The point at fraction `u` of the flight, pushed `off` px along the normal,
 * written into `outPt`. It is `K.at`.
 */
export function atOn(d: Dir, from: Pt, u: number, off: number, outPt: Pt): Pt {
  outPt.x = from.x + d.ux * d.len * u + d.nx * off;
  outPt.y = from.y + d.uy * d.len * u + d.ny * off;
  return outPt;
}

/** The bearing of the shot. It is `HK.ang`. */
export function angOf(c: HitCtx): number {
  return Math.atan2(c.dirY, c.dirX);
}

/** The bearing back to the shooter. It is `HK.back`. */
export function backOf(c: HitCtx): number {
  return Math.atan2(-c.dirY, -c.dirX);
}

/**
 * The arrival point pulled onto the plate: no further than `ONHULL_PX` from
 * the hull centre, written into `outPt`. It is `HK.onHull`.
 *
 * Every hull mark of mockup round 052 lands on the plate however wide the
 * salvo spread was, and this clamp is why. With no hull the arrival point
 * stands as it is.
 */
export function onHull(c: HitCtx, outPt: Pt): Pt {
  const h = c.hull;
  outPt.x = c.at.x; outPt.y = c.at.y;
  if (!h) return outPt;
  const dx = c.at.x - h.x, dy = c.at.y - h.y;
  const len = Math.hypot(dx, dy);
  if (len <= ONHULL_PX) return outPt;
  outPt.x = h.x + (dx / len) * ONHULL_PX;
  outPt.y = h.y + (dy / len) * ONHULL_PX;
  return outPt;
}

/** How far off the hull centre a hull mark may land, in world px. */
export const ONHULL_PX = 6;

/** Which beam the arrival stands off: +1 starboard, −1 port. It is `HK.side`. */
export function sideOf(c: HitCtx): number {
  const h = c.hull;
  if (!h) return 1;
  const s = (c.at.x - h.x) * -Math.sin(h.head) + (c.at.y - h.y) * Math.cos(h.head);
  return s >= 0 ? 1 : -1;
}

/** A fill alpha, held under the ceiling the limits state. */
export const fill = fillFor(COMBAT_LIMITS);

/** A stroke alpha, held under the ceiling the limits state. */
export const stroke = strokeFor(COMBAT_LIMITS);

// ---------------------------------------------------------------------------
// The primitives
// ---------------------------------------------------------------------------
//
// The shared kit's factories, bound once to `[combat.limits]`. Each one
// clamps its alpha, multiplies by `c.alpha` and skips a mark that would draw
// nothing.

/** A hard filled circle. It is `K.disc` and `HK.disc`. */
export const disc = discFor<CombatCtx>(COMBAT_LIMITS);

/** A stroked full circle. It is `K.ring` and `HK.ring`. */
export const ring = ringFor<CombatCtx>(COMBAT_LIMITS);

/** A stroked partial circle, from `start` over `sweep` radians. It is
 *  `K.arc` and `HK.arc`, whose two angle arguments are a start and an END: a
 *  recipe ported from a card passes `a1 - a0` here. */
export const arcOf = arcFor<CombatCtx>(COMBAT_LIMITS);

/** A dashed ring: `n` dashes, each covering `duty` of its own step. */
export const dashedRing = dashedRingFor<CombatCtx>(COMBAT_LIMITS);

/** A round-capped segment. It is `K.line` and `HK.line`. A dash pattern in
 *  world px is left out where it is not wanted; the segment sink has no dash
 *  PHASE, so a card that animates the phase walks the dashes with
 *  `polyline`. */
export const line = lineFor<CombatCtx>(COMBAT_LIMITS);

/**
 * The trail of a round: one segment from the head `h` to `len` px astern of
 * it, down the flight. It is the `K.line(h, K.at(…))` pair most shot cards
 * write.
 */
export function trail(
  c: ShotCtx, h: Pt, len: number, w: number, ink: number, a: number,
): void {
  line(c, h.x, h.y, h.x - c.d.ux * len, h.y - c.d.uy * len, w, ink, a);
}

/** A polyline of `n` points read out of the caller's own scratch `xs` and
 *  `ys`: `n − 1` segments. It is a raw polyline of mockup 048/16, 049/03,
 *  051/17 and 053/15. */
export const polyline = polylineFor<CombatCtx>(line);

/**
 * A jagged run of `n` steps from `(x, y)` along `ang`, each step `step` px
 * long and wobbling `wob` px across. It is `HK.zig`.
 */
export function zig(
  c: CombatCtx, x: number, y: number, ang: number, n: number,
  step: number, wob: number, seed: number, w: number, ink: number, a: number,
): void {
  const cos = Math.cos(ang), sin = Math.sin(ang);
  let px = x, py = y;
  for (let i = 1; i <= n; i++) {
    const off = (hash(seed, i) - 0.5) * 2 * wob;
    const qx = x + cos * step * i - sin * off;
    const qy = y + sin * step * i + cos * off;
    line(c, px, py, qx, qy, w, ink, a);
    px = qx; py = qy;
  }
}

/** One soft additive disc. It is `K.flare` and `HK.flare`. */
export const flare = softFor<CombatCtx>(COMBAT_LIMITS, combatCellKey('flare'));

/** A soft patch squashed to an ellipse and turned to `rot`. It is `HK.fog`. */
export function fog(
  c: CombatCtx, x: number, y: number, rx: number, ry: number,
  rot: number, ink: number, a: number,
): void {
  oval(c, 'flare', c.add, x, y, rx, ry, rot, ink, a);
}

/** One cell squashed to `rx` by `ry` and turned to `rot`, under the fill
 *  clamp: the soft `fog` and the hard `blob`. */
function oval(
  c: CombatCtx, cell: CombatCell, flags: number, x: number, y: number,
  rx: number, ry: number, rot: number, ink: number, a: number,
): void {
  if (rx <= 0 || ry <= 0) return;
  const al = fill(a) * c.alpha;
  if (al <= 0) return;
  const e = c.sprite(combatCellKey(cell));
  c.quads.quad(x, y, 0, 0, 2 * rx, 2 * ry, rot, flags, ink, al, e.u0, e.v0, e.u1, e.v1);
}

/** A dome: clear in the middle and bright on the rim, the shield of mockup
 *  051/12. It is the second baked ramp. */
export const dome = softFor<CombatCtx>(COMBAT_LIMITS, combatCellKey('dome'));

/** A hard filled ellipse turned to `rot`. It is `K.blob`. */
export function blob(
  c: CombatCtx, x: number, y: number, rx: number, ry: number,
  rot: number, ink: number, a: number,
): void {
  oval(c, 'disc', 0, x, y, rx, ry, rot, ink, a);
}

/** A flat rectangle of `w` by `h`, centred on the point and turned to `rot`. */
export const rect = rectFor<CombatCtx>(COMBAT_LIMITS);

/** A filled dart, its NOSE at `(x, y)`, turned to `ang`, `len` long and `hw`
 *  half wide. It is `K.dart`. */
export const dart = apexFor<CombatCtx>(COMBAT_LIMITS, combatCellKey('dart'), COMBAT_DART_LEN);

/**
 * A fragment plate of half-length `s`, turned to `rot` and thinned to `thin`
 * of its width. It is `HK.poly` of mockup 052/04.
 */
export function plate(
  c: CombatCtx, x: number, y: number, rot: number,
  s: number, thin: number, ink: number, a: number,
): void {
  if (s <= 0 || thin <= 0) return;
  const al = fill(a) * c.alpha;
  if (al <= 0) return;
  const e = c.sprite(combatCellKey('plate'));
  const sx = s / COMBAT_PLATE_HALF, sy = (s / COMBAT_PLATE_HALF) * thin;
  c.quads.quad(
    x, y, e.ox * sx, e.oy * sy, e.w * sx, e.h * sy, rot, 0, ink, al,
    e.u0, e.v0, e.u1, e.v1,
  );
}

/** A four-point glint: two crossed segments. It is `K.glint`. */
export function glint(
  c: CombatCtx, x: number, y: number, r: number, w: number, ink: number, a: number,
): void {
  line(c, x - r, y, x + r, y, w, ink, a);
  line(c, x, y - r, x, y + r, w, ink, a);
}

/** A hex outline of radius `r`, turned to `rot`: six segments. */
export function hexOutline(
  c: CombatCtx, x: number, y: number, r: number, rot: number,
  w: number, ink: number, a: number,
): void {
  if (r <= 0) return;
  let px = x + Math.cos(rot) * r, py = y + Math.sin(rot) * r;
  for (let i = 1; i <= 6; i++) {
    const th = rot + (i / 6) * TAU;
    const qx = x + Math.cos(th) * r, qy = y + Math.sin(th) * r;
    line(c, px, py, qx, qy, w, ink, a);
    px = qx; py = qy;
  }
}

/**
 * The victim's own silhouette, at `(x, y)`, turned to `head`, scaled `sx`
 * along its axis and `sy` across it. It is `HK.hull`.
 *
 * A scale may be small or negative: a card that rolls a hull over passes
 * `sy = cos(...)` through zero. `HULL_SCALE_FLOOR` keeps the quad from
 * collapsing, which is the card's own guard.
 *
 * `clipX0`/`clipX1` keep one slice of the hull in ITS OWN x, in world px at
 * scale 1. The cell is axis-aligned in hull space, so the slice is a
 * sub-rectangle of the cell: the u range and the box both narrow, and the
 * picture is exact. It is the split of mockup 053/02 and the bow slice of
 * 053/03; nothing else in the kit clips.
 */
export function hull(
  c: CombatCtx, h: HullMark, x: number, y: number, head: number,
  sx: number, sy: number, ink: number, a: number, opts: HullDrawOpts = EMPTY_OPTS,
): void {
  const al = fill(a) * c.alpha;
  if (al <= 0) return;
  let kx = sx, ky = sy;
  if (Math.abs(kx) < HULL_SCALE_FLOOR) kx = kx < 0 ? -HULL_SCALE_FLOOR : HULL_SCALE_FLOOR;
  if (Math.abs(ky) < HULL_SCALE_FLOOR) ky = ky < 0 ? -HULL_SCALE_FLOOR : HULL_SCALE_FLOOR;
  const flags = opts.add ? c.add : 0;
  if (opts.halo) {
    const b = h.bloom();
    c.quads.quad(
      x, y, b.ox * kx, b.oy * ky, b.w * kx, b.h * ky, head, c.add, ink,
      al * HULL_HALO_ALPHA, b.u0, b.v0, b.u1, b.v1,
    );
  }
  const e = h.sprite(opts.hollow === true);
  // The cell's own span in hull x, which the slice is taken out of.
  const x0 = e.ox - e.w / 2, x1 = e.ox + e.w / 2;
  let a0 = x0, a1 = x1;
  if (opts.clipX0 !== undefined && opts.clipX0 > a0) a0 = opts.clipX0;
  if (opts.clipX1 !== undefined && opts.clipX1 < a1) a1 = opts.clipX1;
  if (a1 <= a0) return;
  const u0 = e.u0 + ((e.u1 - e.u0) * (a0 - x0)) / e.w;
  const u1 = e.u0 + ((e.u1 - e.u0) * (a1 - x0)) / e.w;
  const cx = (a0 + a1) / 2, w = a1 - a0;
  c.quads.quad(
    x, y, cx * kx, e.oy * ky, w * kx, e.h * ky, head, flags, ink, al,
    u0, e.v0, u1, e.v1,
  );
}

/** The smallest scale a hull quad is drawn at, so it never collapses. */
export const HULL_SCALE_FLOOR = 0.02;

/** How faint the halo stands under a hull a recipe redraws (`fleets.ts`). */
const HULL_HALO_ALPHA = 0.42;

const EMPTY_OPTS: HullDrawOpts = {};

/**
 * The wreck of the sea chart (R801), as `render/chart-glyphs.ts` draws it.
 *
 * A sink look may FLASH it while the descent runs. It must not leave one: the
 * wreck layer owns the lasting mark (R801 to R805), and a second mark on the
 * same site would read as two ships.
 */
export { wreck };
